import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import {
  AuthorizationStates,
  LeaderboardType,
  ModulesStates,
  VisibilityStates,
} from '@racemap/utilities/consts/events';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { EventLoadTypes, type RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import UAParser from 'ua-parser-js';
import { useInterval } from 'usehooks-ts';
import { NotActivatedOverlay } from '../components/BasicComponents/NotActivatedOverlay/NotActivatedOverlay';
import { PageSpinner } from '../components/BasicComponents/PageSpinner/PageSpinner';
import { RacemapLogo } from '../components/BasicComponents/RacemapLogo';
import { Header } from '../components/Leaderboard/Header';
import { Leaderboard } from '../components/Leaderboard/Leaderboard/Leaderboard';
import { getLeaderboardUrl } from '../lib/EventUtils';
import { useQuery, useTabVisibility } from '../lib/customHooks';
import { useDocumentTitle } from '../lib/customHooks/useDocumentTitle';
import { useStore } from '../store/reducers';

type Params = {
  eventSlug: string;
};

const apiClient = RacemapAPIClient.fromWindowLocation(false);

const userAgent = new UAParser();
const RELOAD_INTERVAL = 1 * OneMinuteInMillis;

export function LeaderboardView() {
  const { eventSlug } = useParams<Params>();

  const relevantEvents = useStore((s) => s.leaderboard.getter.relevantEventsData());
  const selectedEventId = useStore((s) => s.leaderboard.selectedEventId);
  const loading = useStore((s) => s.leaderboard.isLoading);
  const selectedEventData = useStore((s) => s.leaderboard.getter.selectedEventData());
  const parentEventData = useStore((s) => s.leaderboard.getter.parentEventData());
  const selectedEvent = selectedEventData?.eventObject;
  const parentEvent = parentEventData?.eventObject;

  const { loadEventData, changeSelectedEvent } = useStore((s) => s.leaderboard.actions);
  const isTabVisible = useTabVisibility();

  const query = useQuery();
  const embedded = query.get('embed') === 'true';
  const isMobile = userAgent.getDevice().type === 'mobile';
  const [, endTime] = selectedEvent != null ? currentEventTimes(selectedEvent) : [];
  const eventIsOver = endTime != null && endTime < Date.now();
  const isNotActivated =
    selectedEvent?.authorization === AuthorizationStates.NONE ||
    selectedEvent?.modules.timing.state === ModulesStates.NONE;
  const loadInterval = isTabVisible && !eventIsOver ? RELOAD_INTERVAL : null;
  const selectedEventName = selectedEvent?.name;
  const navigate = useNavigate();
  const [triggeredLoad, setTriggeredLoad] = useState<Set<string>>(new Set());

  async function triggerLoad(selectedEvent: Immutable<RacemapEvent>) {
    if (
      selectedEvent?.visibility !== VisibilityStates.ARCHIVED &&
      selectedEvent?.modules?.timing?.state === ModulesStates.ACTIVATED &&
      selectedEvent?.authorization !== AuthorizationStates.NONE &&
      selectedEvent.modules.timing.leaderboard.type === LeaderboardType.DEFAULT
    ) {
      await apiClient.triggerEventLoad(selectedEvent.id, EventLoadTypes.TIMING_LOADS);
      setTriggeredLoad(() => new Set([...triggeredLoad, selectedEvent.id]));
    }
  }

  useEffect(() => {
    if (eventSlug == null) return;
    loadEventData(eventSlug);
  }, [eventSlug]);

  useEffect(() => {
    const event = Array.from(relevantEvents.values()).find((e) => e.eventObject.slug === eventSlug);
    if (event != null) {
      changeSelectedEvent(event.eventObject.id);
    } else if (relevantEvents.size > 0) {
      const firstEvent = relevantEvents.values().next().value;
      changeSelectedEvent(firstEvent.eventObject.id);
    }

    if (selectedEvent != null && !triggeredLoad.has(selectedEvent.id)) {
      triggerLoad(selectedEvent);
    }
  }, [relevantEvents]);

  useInterval(() => eventSlug && loadEventData(eventSlug), loadInterval);

  useDocumentTitle(
    `${!isEmptyString(selectedEventName) ? selectedEventName : 'Racemap'} | Leaderboard`,
  );

  const handleSelectedEventChange = (newEventId: string, newSlug?: string) => {
    navigate(`/timing/${newSlug || newEventId}`);
  };

  if (selectedEvent == null) return <PageSpinner />;

  // If we land on this page but set a external in the config
  if (selectedEvent.modules.timing.leaderboard.type !== LeaderboardType.DEFAULT) {
    window.location.href = getLeaderboardUrl(selectedEvent);
  }

  if (selectedEvent.visibility === VisibilityStates.ARCHIVED) {
    return <div>This Leaderboard has been archived.</div>;
  }
  if (selectedEvent.modules.timing.state === ModulesStates.ENABLED) {
    return <div>This Leaderboard is disabled.</div>;
  }

  return (
    <Container>
      {!embedded && (
        <Header
          events={Array.from(relevantEvents.values()).map((e) => e.eventObject)}
          selectedEventId={selectedEventId}
          parentEvent={parentEvent}
          onSelectedEventChange={handleSelectedEventChange}
          loading={loading}
        />
      )}
      <LeaderBoardContainer embedded={embedded || isMobile}>
        <Leaderboard embedded={embedded || isMobile} />
        {!embedded && !isMobile && (
          <Footer>
            <RacemapLogo height={40} />
          </Footer>
        )}
      </LeaderBoardContainer>
      {isNotActivated && <NotActivatedOverlay />}
    </Container>
  );
}

const Container = styled.div`
  background: white;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: medium;
  background-image: url(https://racemap.com/dist/bg-QIQWCRK6.svg);
`;

const LeaderBoardContainer = styled.div<{ embedded: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px;

  @media screen and (min-width: 576px) {
    padding-left: 30px;
    padding-right: 30px;
  }

  @media screen and (min-height: 800px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  ${({ embedded }) => embedded && 'padding: 0 !important;'}
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
`;
